import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import {
  Grid,
  List,
  ListItem,
  Divider,
  Chip,
  Typography,
  ListItemAvatar,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Box,
  LinearProgress,
  Tooltip,
  IconButton,
} from "@mui/material";
import PairingEventDialog from "./PairingEventDialog";
import NewEventDialog from "../../../components/NewEventDialog/NewEventDialog";
import formatDate from "../../../utilities/formatDate";
import { EventContext } from "../../../contexts/EventContext";
import request from "../../../utilities/requests";

const channelLogos = {
  tnfm: "https://d19w6ranc5yet4.cloudfront.net/tnfm/tnfm-logo.png",
  eventbrite: "https://d19w6ranc5yet4.cloudfront.net/tnfm/eb-logo.png",
};

const PairingEvents = () => {
  const navigate = useNavigate();
  const { userData, pairingEvents, setPairingEvents } =
    useContext(EventContext);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [orgList, setOrgList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [all, setAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState("upcoming");
  const [progressDisplay, setProgressDisplay] = useState({});
  const [editEvent, setEditEvent] = useState(null);

  useEffect(() => {
    if (!loaded) {
      getPairingEvents();
      setLoaded(true);
    }
  });

  useEffect(() => {
    if (pairingEvents && pairingEvents.length > 0) {
      applyFilters(pairingEvents, selectedOrg, statusFilter);
    }
  }, [pairingEvents, selectedOrg, statusFilter]);
  
  const applyFilters = (events, orgId, status) => {
    let result = [...events];

    // Org filter
    if (orgId) {
      result = result.filter((f) => f?.event?.org === orgId);
    }

    // Status filter
    if (status === "upcoming") {
      result = result.filter(
        (f) => f?.event?.draft === false || f?.event?.draft === undefined
      );
    } else if (status === "draft") {
      result = result.filter((f) => f?.event?.draft === true);
    }
    // "all" shows everything

    setFilteredEvents(result);
  };

  useEffect(() => {
    if (userData?.role === "superadmin" && orgList.length > 0) {
      setShowFilter(true);
      handleOrgChange(userData?.org?._id);
    }
  }, [userData, orgList, pairingEvents]);

  const getPairingEvents = async (allValue) => {
    let url = "pairingevent";
    if (allValue === true) {
      url = url + "?all=true";
    }
    const result = await request(url);

    if (result.success) {
      setPairingEvents(result.pairingEvents);
      setFilteredEvents(result.pairingEvents);
      if (result?.orgData) {
        setOrgList(result.orgData);
      }
    }
  };

  const getListItemColor = (pairingEvent) => {
    try {
      const userTimezone = moment.tz.guess(); // e.g., "America/Los_Angeles"
      const now = new moment().tz(userTimezone);
      const date = moment(pairingEvent?.event?.date).tz(userTimezone);
      const isSameDay = now.isSame(date, "day");
      if (isSameDay) {
        return "rgb(222 221 221)";
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const handleClickOpen = (pairingEvent) => {
    navigate(`/eventcontroller/${pairingEvent?.event?._id}`);
  };

  const handleAll = () => {
    setAll(!all);
    getPairingEvents(!all);
  };

  const handleOrgChange = (orgId) => {
    if (orgId === "none") {
      setSelectedOrg(null);
      applyFilters(pairingEvents, null, statusFilter);
    } else {
      setSelectedOrg(orgId);
      applyFilters(pairingEvents, orgId, statusFilter);
    }
  };
  const handleStatusChange = (value) => {
    // Special case: fetch ALL events (past + future)
    if (value === "all_events") {
      handleAll();
      return;
    }

    setStatusFilter(value);
    applyFilters(pairingEvents, selectedOrg, value);
  };

  const handleChannelClick = (e, pairingEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setEditEvent(pairingEvent?.event || null);
  };

  const renderChannels = (pairingEvent) => {
    try {
      const channels = pairingEvent?.channels;
      if (channels?.length > 0) {
        return (
          <Tooltip title="Go to ticketing">
            <IconButton
              size="small"
              onClick={(e) => handleChannelClick(e, pairingEvent)}
              sx={{ p: 0.75, borderRadius: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {channels.map((channel) => (
                  <Box
                    key={channel}
                    component="img"
                    src={channelLogos[channel]}
                    alt={`${channel} logo`}
                    sx={{
                      height: 16,
                      width: "auto",
                      objectFit: "contain",
                      display: "block",
                      maxWidth: "100%",
                    }}
                  />
                ))}
              </Stack>
            </IconButton>
          </Tooltip>
        );
      }

      return null;
    } catch (e) {
      return null;
    }
  };

  const toggleProgressDisplay = (eventId) => {
    setProgressDisplay((prev) => ({
      ...prev,
      [eventId]: prev[eventId] === "percent" ? "sold" : "percent",
    }));
  };

  const renderProgress = (pairingEvent) => {
    const eventId = pairingEvent?._id;
    const sold = pairingEvent?.ticketsSold || 0;
    const capacity = pairingEvent?.capacity || 0;
    const percent = pairingEvent?.percentSold || 0;

    const mode = progressDisplay[eventId] || "sold";
    const displayValue = mode === "percent" ? `${percent}%` : `${sold} / ${capacity}`;

    return (
      <Tooltip title="Click to toggle sold / percent">
        <Box
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleProgressDisplay(eventId);
          }}
          sx={{
            cursor: "pointer",
            width: "100%",
            mt: 1,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", sm: "100%", md: 420, lg: 480 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Tickets
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {displayValue}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{
                height: 6,
                borderRadius: 3,
              }}
            />
          </Box>
        </Box>
      </Tooltip>
    );
  };

  return (
    <>
      <Grid
        container
        flexDirection="column"
        sx={{ width: "100%", overflowX: "hidden" }}
      >
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <FormControl
            variant="outlined"
            sx={{
              width: { xs: "100%", sm: 220 },
            }}
          >
            <Select
              value={statusFilter}
              onChange={(e) => handleStatusChange(e.target.value)}
              sx={{ height: 40 }}
            >
              <MenuItem value="upcoming">Upcoming Events</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="all">Upcoming + Draft</MenuItem>
              <MenuItem value="all_events">All Events</MenuItem>
            </Select>
          </FormControl>
          {showFilter ? (
            <FormControl
              fullWidth
              variant="outlined"
              sx={{
                width: { xs: "100%", sm: 250 },
              }}
            >
              <InputLabel sx={{ top: -5 }}>Org Filter</InputLabel>
              <Select
                value={selectedOrg || ""}
                label="Org"
                onChange={(e) => handleOrgChange(e.target.value)}
                sx={{ height: 40 }}
              >
                <MenuItem value={"none"}>None</MenuItem>
                {orgList.map((org) => {
                  return (
                    <MenuItem key={org._id} value={org._id}>
                      {org.name}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          ) : null}
        </Grid>
        <Grid item>
          <List>
            {filteredEvents.map((pairingEvent) => {
              return (
                <div key={pairingEvent._id} style={{ padding: 0 }}>
                  <ListItem
                    style={{
                      backgroundColor: getListItemColor(pairingEvent),
                      padding: 10,
                    }}
                  >
                    {pairingEvent?.logo ? (
                      <ListItemAvatar style={{ marginRight: 20 }}>
                        <Avatar src={pairingEvent.logo} />
                      </ListItemAvatar>
                    ) : null}
                    <a
                      href={`/eventcontroller/${pairingEvent?.event?._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        textDecoration: "none",
                        color: "inherit",
                        width: "100%",
                        display: "block",
                        overflow: "hidden",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleClickOpen(pairingEvent);
                      }}
                    >
                      <Grid container spacing={1}>
                        <Grid item xs={12} style={{display: 'flex', flexDirection: 'row', alignItems:'center', gap:'10px'}}>
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {pairingEvent?.event?.name}
                          </Typography>
                          {pairingEvent?.event?.draft && <Chip label="DRAFT" variant="outlined" size="small"/>}
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            {formatDate(pairingEvent?.event?.date)}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "12px",
                          }}
                        >
                          {renderChannels(pairingEvent)}
                        </Grid>

                        <Grid item xs={12}>
                          {renderProgress(pairingEvent)}
                        </Grid>
                      </Grid>
                    </a>
                  </ListItem>
                  <Divider />
                </div>
              );
            })}
          </List>
        </Grid>
      </Grid>
      <NewEventDialog
        open={!!editEvent}
        close={() => setEditEvent(null)}
        eventData={editEvent}
        initialTab={userData?.org?.config?.ticketingEnabled ? 1 : 0}
      />
    </>
  );
};

export default PairingEvents;
